import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { User, Lock, KeyRound, Loader2, AlertCircle, ArrowLeft, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const ForgotPassword = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState<1 | 2>(1);
  const [identifier, setIdentifier] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const navigate = useNavigate();
  const API_URL = 'https://atrons.com.et/api/auth'; 

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');
    setLoading(true);

    try {
      await axios.post(`${API_URL}/forgot-password`, { identifier });
      setSuccessMsg(t('code_sent_msg', 'If an account exists, a 6-digit code has been sent to your email or phone.'));
      setStep(2); 
    } catch (err: any) {
      setError(err.response?.data?.message || t('code_request_failed', 'Failed to request reset code. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.post(`${API_URL}/reset-password`, { 
        identifier, 
        code, 
        newPassword 
      });
      navigate('/login?reason=password_changed');
    } catch (err: any) {
      setError(err.response?.data?.message || t('invalid_code_msg', 'Invalid code or failed to reset password.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page-wrapper px-4 py-8 min-h-screen overflow-y-auto pb-24 flex items-center justify-center bg-slate-950">
      <div className="auth-card max-w-md w-full bg-slate-900 border border-white/10 p-8 rounded-3xl shadow-2xl">
        
        <div className="mb-8">
          <Link to="/login" className="inline-flex items-center text-slate-400 hover:text-white transition-colors text-xs font-bold uppercase tracking-widest mb-6">
            <ArrowLeft size={16} className="mr-2" /> {t('back_to_login', 'Back to Login')}
          </Link>
          <h2 className="text-2xl font-black text-white tracking-tighter uppercase italic">
            {t('recover', 'Recover')} <span className="text-blue-500">{t('account', 'Account')}</span>
          </h2>
          <p className="mt-2 text-slate-400 font-bold text-[10px] uppercase tracking-[0.15em]">
            {step === 1 ? t('step_1_recover', 'Step 1: Request Security Code') : t('step_2_recover', 'Step 2: Verify & Reset')}
          </p>
        </div>

        {error && (
          <div className="mb-6 bg-red-500/10 border-l-4 border-red-500 p-4 rounded-xl text-red-400 text-xs font-bold animate-shake">
            <AlertCircle size={18} className="inline mr-2" /> {error}
          </div>
        )}

        {successMsg && step === 2 && (
          <div className="mb-6 bg-blue-500/10 border-l-4 border-blue-500 p-4 rounded-xl text-blue-400 text-xs font-bold">
            <CheckCircle size={18} className="inline mr-2" /> {successMsg}
          </div>
        )}

        {step === 1 && (
          <form className="space-y-6" onSubmit={handleRequestCode}>
            <div className="relative">
              <label className="text-[10px] font-black uppercase text-slate-500 ml-4 mb-2 block tracking-widest">
                {t('registered_email_phone', 'Registered Email or Phone')}
              </label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                <input
                  type="text"
                  required
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  className="auth-input pl-12 w-full bg-slate-950 border border-white/5 rounded-2xl py-4 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all outline-none"
                  placeholder={t('email_phone_placeholder', '09... or name@example.com')}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-2xl font-black uppercase tracking-widest shadow-xl transition-all active:scale-95 disabled:bg-slate-800 disabled:text-slate-600 flex items-center justify-center gap-3"
            >
              {loading ? <Loader2 className="animate-spin h-5 w-5" /> : t('send_reset_code', 'Send Reset Code')}
            </button>
          </form>
        )}

        {step === 2 && (
          <form className="space-y-6" onSubmit={handleResetPassword}>
            <div className="space-y-5">
              <div className="relative">
                <label className="text-[10px] font-black uppercase text-slate-500 ml-4 mb-2 block tracking-widest">
                  {t('six_digit_code', '6-Digit Reset Code')}
                </label>
                <div className="relative">
                  <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                  <input
                    type="text"
                    required
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    className="auth-input pl-12 w-full bg-slate-950 border border-white/5 rounded-2xl py-4 text-white tracking-[0.5em] font-mono focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all outline-none"
                    placeholder="••••••"
                  />
                </div>
              </div>

              <div className="relative">
                <label className="text-[10px] font-black uppercase text-slate-500 ml-4 mb-2 block tracking-widest">
                  {t('new_password', 'New Password')}
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                  <input
                    type="password"
                    required
                    minLength={6}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="auth-input pl-12 w-full bg-slate-950 border border-white/5 rounded-2xl py-4 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all outline-none"
                    placeholder={t('enter_new_password', 'Enter new password')}
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-emerald-600 hover:bg-emerald-500 text-white rounded-2xl font-black uppercase tracking-widest shadow-xl transition-all active:scale-95 disabled:bg-slate-800 disabled:text-slate-600 flex items-center justify-center gap-3"
            >
              {loading ? <Loader2 className="animate-spin h-5 w-5" /> : t('confirm_new_password', 'Confirm New Password')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;